import { createTheme } from '@material-ui/core/styles';

// A custom theme for this app
const theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 440,
      md: 768,
      lg: 1024,
      xl: 1440,
    },
  },
  palette: {
    primary: {
      main: '#556cd6',
      light: '#005EDC',
      dark: '#001871',
    },
    secondary: {
      main: '#19857b',
    },
    error: {
      main: '#ED1D24',
      warning: '#FF6B00',
    },
    background: {
      default: '#F7F8F9',
    },
    grayScale: {
      dark: '#4A4A4A',
      main: '#787A7E',
      light: '#F5F5F5',
      disabled: '#7B7B7B',
      middleLight: '#BBBBBB',
      lowMiddleLight: '#535353',
      placeholder: '#9F9F9F',
    },
    white: '#FFF',
    black: '#000',
    blue1: '#061556',
    blue2: '#005EDC',
    blue3: '#0045C3',
    blue4: '#E1ECFA',
    divider: '#E5E5E5',
    yellow: '#FEBD11',
    grayBlue: '#b6b7b9',
    grey1: '#4A4A4A',
    grey2: '#787A7E',
    grey4: '#F5F5F5',
    grey5: '#F7F8F9',
    grey6: '#BDBEC0',
    grey7: '#BBBBBB',
    grey8: '#9F9F9F',
    grey9: '#535353',
    critical: '#ED1D24',
    critical2: '#ED1C24',
    alert: '#FEA01C',
    successful: '#01C267',
    shadow: '#06155626',
    border: '#CCCED8',
    green: '#05A56B',
    green1: '#1c9421',
  },
  typography: {
    proxima: {
      regular: 'Proxima, Roboto, Arial, sans-serif',
      bold: 'ProximaBold, Roboto, Arial, sans-serif',
      light: 'ProximaLight, Roboto, Arial, sans-serif',
      semiBold: 'ProximaSemiBold, Roboto, Arial, sans-serif',
    },
    kapra: {
      regular: 'Kapra',
    },
    fontFamily: 'Proxima, Roboto, Arial, sans-serif',
    size: {
      0: '0.75rem', // 12px
      0.5: '0.875rem', // 14px
      1: '0.875rem', // 14px
      1.5: '1rem', // 16px
      2: '1rem', // 16px
      3: '1.125rem', // 18px
      4: '1.25rem', // 20px
      5: '1.375rem', // 22px
      6: '1.5rem', // 24px
      6.5: '1.625rem', // 26px
      7: '1.875rem', // 30px
      8: '1.875rem', // 34px
      9: '2.25rem', // 36px
      10: '2.5rem', // 40px
      11: '3rem', // 48px
      12: '4rem', // 64px
      13: '4.5rem', // 72px
    },
    h1: {
      fontFamily: 'ProximaSemiBold, Roboto, Arial, sans-serif',
      fontSize: () => {
        return theme.typography.pxToRem('64');
      },
      letterSpacing: () => {
        return theme.typography.pxToRem('0');
      },
      lineHeight: '64px',
    },
    h2: {
      fontFamily: 'Proxima, Roboto, Arial, sans-serif',
      fontSize: () => {
        return theme.typography.pxToRem('48');
      },
      letterSpacing: () => {
        return theme.typography.pxToRem('0');
      },
      lineHeight: '48px',
    },
    h3: {
      fontFamily: 'Proxima, Roboto, Arial, sans-serif',
      fontSize: () => {
        return theme.typography.pxToRem('40');
      },
      letterSpacing: () => {
        return theme.typography.pxToRem('0');
      },
      lineHeight: () => {
        return theme.typography.pxToRem('40');
      },
    },
    h4: {
      fontFamily: 'Proxima, Roboto, Arial, sans-serif',
      fontSize: () => {
        return theme.typography.pxToRem('32');
      },
      lineHeight: () => {
        return theme.typography.pxToRem('39');
      },
    },
    h5: {
      fontFamily: 'Proxima, Roboto, Arial, sans-serif',
      fontSize: () => {
        return theme.typography.pxToRem('24');
      },
      lineHeight: () => {
        return theme.typography.pxToRem('24');
      },
      textTransform: 'none',
    },
    h6: {
      fontFamily: 'Proxima, Roboto, Arial, sans-serif',
      fontSize: () => {
        return theme.typography.pxToRem('20');
      },
    },
    subtitle1: {
      fontFamily: 'Proxima, Roboto, Arial, sans-serif',
      fontSize: () => {
        return theme.typography.pxToRem('18');
      },
      letterSpacing: () => {
        return theme.typography.pxToRem('0');
      },
      lineHeight: () => {
        return theme.typography.pxToRem('27');
      },
      textTransform: 'uppercase',
    },
  },
  spacing: 10,
  rccl: {
    fontFamily: 'Proxima, Roboto, Arial, sans-serif',
    body1: {
      fontFamily: 'Proxima, Roboto, Arial, sans-serif',
      fontSize: () => {
        return theme.typography.pxToRem('18');
      },
      lineHeight: () => {
        return theme.typography.pxToRem('27');
      },
    },
    body2: {
      color: () => {
        return theme.palette.grey2;
      },
      fontFamily: 'Proxima, Roboto, Arial, sans-serif',
      fontSize: () => {
        return theme.typography.pxToRem('16');
      },
      lineHeight: () => {
        return theme.typography.pxToRem('24');
      },
    },
    body3: {
      fontFamily: 'Proxima, Roboto, Arial, sans-serif',
      fontSize: () => {
        return theme.typography.pxToRem('14');
      },
      lineHeight: () => {
        return theme.typography.pxToRem('18');
      },
    },
    button: {
      fontFamily: 'ProximaBold, Roboto, Arial, sans-serif',
      fontSize: () => {
        return theme.typography.pxToRem('12');
      },
      letterSpacing: () => {
        return theme.typography.pxToRem('2');
      },
      lineHeight: () => {
        return theme.typography.pxToRem('16');
      },
      textTransform: 'uppercase',
    },
    caption: {
      fontFamily: 'Proxima, Roboto, Arial, sans-serif',
      fontSize: () => {
        return theme.typography.pxToRem('12');
      },
      lineHeight: () => {
        return theme.typography.pxToRem('16');
      },
    },
    formError: {
      color: () => {
        return theme.palette.critical;
      },
      fontFamily: 'ProximaLight, Roboto, Arial, sans-serif',
      fontSize: () => {
        return theme.typography.pxToRem('11');
      },
      lineHeight: () => {
        return theme.typography.pxToRem('16');
      },
      margin: () => {
        return `${theme.typography.pxToRem('2')} 0 0`;
      },
      textAlign: 'left',
    },
    labelLarge: {
      fontFamily: 'ProximaBold, Roboto, Arial, sans-serif',
      fontSize: () => {
        return theme.typography.pxToRem('16');
      },
      letterSpacing: () => {
        return theme.typography.pxToRem('2');
      },
      lineHeight: () => {
        return theme.typography.pxToRem('24');
      },
    },
    labelMedium: {
      fontFamily: 'Proxima, Roboto, Arial, sans-serif',
      fontSize: () => {
        return theme.typography.pxToRem('14');
      },
      letterSpacing: () => {
        return theme.typography.pxToRem('2');
      },
      lineHeight: () => {
        return theme.typography.pxToRem('18');
      },
    },
    labelMediumBold: {
      fontFamily: 'ProximaBold, Roboto, Arial, sans-serif',
      fontSize: () => {
        return theme.typography.pxToRem('14');
      },
      letterSpacing: () => {
        return theme.typography.pxToRem('0');
      },
      lineHeight: () => {
        return theme.typography.pxToRem('18');
      },
    },
    labelSmall: {
      fontFamily: 'Proxima, Roboto, Arial, sans-serif',
      fontSize: () => {
        return theme.typography.pxToRem('12');
      },
      letterSpacing: () => {
        return theme.typography.pxToRem('0');
      },
      lineHeight: () => {
        return theme.typography.pxToRem('16');
      },
    },
    labelSmallBold: {
      fontFamily: 'ProximaBold, Roboto, Arial, sans-serif',
      fontSize: () => {
        return theme.typography.pxToRem('12');
      },
      letterSpacing: () => {
        return theme.typography.pxToRem('0');
      },
      lineHeight: () => {
        return theme.typography.pxToRem('16');
      },
    },
    labelMicro: {
      fontFamily: 'Proxima, Roboto, Arial, sans-serif',
      fontSize: () => {
        return theme.typography.pxToRem('14');
      },
      letterSpacing: () => {
        return theme.typography.pxToRem('0');
      },
      lineHeight: () => {
        return theme.typography.pxToRem('17');
      },
    },
    link: {
      fontFamily: 'Proxima, Roboto, Arial, sans-serif',
      fontSize: () => {
        return theme.typography.pxToRem('14');
      },
      lineHeight: () => {
        return theme.typography.pxToRem('18');
      },
    },
    subtitle1: {
      fontFamily: 'Proxima, Roboto, Arial, sans-serif',
      fontSize: () => {
        return theme.typography.pxToRem('18');
      },
      letterSpacing: () => {
        return theme.typography.pxToRem('0');
      },
      lineHeight: () => {
        return theme.typography.pxToRem('27');
      },
      textTransform: 'uppercase',
    },
    subtitle2: {
      fontFamily: 'ProximaBold, Roboto, Arial, sans-serif',
      fontSize: () => {
        return theme.typography.pxToRem('16');
      },
      lineHeight: () => {
        return theme.typography.pxToRem('24');
      },
      textTransform: 'uppercase',
    },
    subtitleMedium: {
      fontSize: () => {
        return theme.typography.pxToRem('20');
      },
    },
    subtitleLarge: {
      fontSize: () => {
        return theme.typography.pxToRem('24');
      },
    },
  },
  overrides: {
    MuiInput: {
      root: {
        '-webkit-appearance': 'none',
        '-moz-appearance': 'none',
        appearance: 'none',
        'background-clip': 'padding-box',
      },
    },
    MuiPaper: {
      elevation1: {
        border: 'none',
        boxShadow: '0 2px 4px 0 #06155626',
      },
      rounded: {
        borderRadius: '5px',
      },
    },
    MuiTableCell: {
      root: {
        padding: 0,
        borderBottom: 'none',
        '&:last-child': {
          paddingRight: 0,
        },
      },
    },
    MuiPickersToolbar: {
      toolbar: {
        backgroundColor: () => {
          return theme.palette.grey7;
        },
      },
    },
    MuiPickersCalendarHeader: {
      switchHeader: {
        backgroundColor: () => {
          return theme.palette.grey4;
        },
        color: () => {
          return theme.palette.blue1;
        },
      },
    },
    MuiPickersDay: {
      day: {
        color: () => {
          return theme.palette.blue2;
        },
      },
      daySelected: {
        backgroundColor: () => {
          return theme.palette.blue1;
        },
      },
      dayDisabled: {
        color: () => {
          return theme.palette.grey7;
        },
      },
      current: {
        color: () => {
          return theme.palette.grayBlue;
        },
      },
    },
    MuiPickersModal: {
      dialogAction: {
        color: () => {
          return theme.palette.blue2;
        },
      },
    },
  },
});

export default theme;
